import { useState } from "react";
import { Building2, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SkeletonRows } from "@/components/ui/skeleton";
import { date, num, relativeTime } from "@/lib/format";

import {
  errorText,
  useCreateTenant,
  usePlatformTenants,
  useSetTenantEnabled,
  type NewTenant,
  type TenantSummary,
} from "./api";

/**
 * Platform console (super tenant): every lender tenant with its vital signs;
 * create one, disable (freeze) or enable it. Only aggregates are shown —
 * counts and dates, never a lender's records.
 */
export function TenantsPage() {
  const tenants = usePlatformTenants();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
            Platform
          </p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
            <Building2 className="h-5 w-5" /> Tenants
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every lender on GraviCollect. Create one, or disable it to block
            sign-in for all its users while keeping its data. Every action is in
            the audit log.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setShowCreate(true)}
          disabled={showCreate}
        >
          <Plus className="h-3.5 w-3.5" /> New tenant
        </Button>
      </div>

      {showCreate && <CreateTenantForm onClose={() => setShowCreate(false)} />}

      <Card>
        <CardHeader>
          <CardTitle>All tenants</CardTitle>
          <CardDescription>
            {tenants.data
              ? `${num(tenants.data.length)} tenants · ${num(tenants.data.filter((t) => t.status === "ACTIVE").length)} active`
              : "—"}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {tenants.isPending ? (
            <div className="p-5">
              <SkeletonRows rows={4} />
            </div>
          ) : tenants.isError ? (
            <p className="p-5 text-sm text-negative">
              Couldn't load tenants:{" "}
              {errorText(tenants.error, "the server gave no reason.")}
            </p>
          ) : tenants.data.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground">
              No tenants yet. Create the first one above.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-2xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-5 py-2 font-semibold">Tenant</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Users
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Active 30d
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Open cases
                    </th>
                    <th className="px-3 py-2 font-semibold">
                      Latest dues file
                    </th>
                    <th className="px-3 py-2 font-semibold">Last sign-in</th>
                    <th className="px-5 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {tenants.data.map((t) => (
                    <TenantRow key={t.tenantId} t={t} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TenantRow({ t }: { t: TenantSummary }) {
  const toggle = useSetTenantEnabled();
  const frozen = t.status === "FROZEN";

  function onToggle() {
    if (frozen) {
      if (
        window.confirm(
          `Enable ${t.displayName}? Its users can sign in again straight away.`,
        )
      ) {
        toggle.mutate({ slug: t.slug, enable: true });
      }
      return;
    }
    const reason = window.prompt(
      `Disable ${t.displayName}? Every user is signed out and cannot sign in; background jobs stop; data is kept.\n\nReason (recorded in the audit log):`,
    );
    if (reason && reason.trim())
      toggle.mutate({ slug: t.slug, enable: false, reason: reason.trim() });
  }

  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="px-5 py-2.5">
        <div className="font-medium text-navy-900">{t.displayName}</div>
        <div className="font-mono text-2xs text-muted-foreground">
          {t.slug} · {t.tier} · since {date(t.createdAt)}
        </div>
      </td>
      <td className="px-3 py-2.5">
        <Badge
          tone={
            t.status === "ACTIVE" ? "positive" : frozen ? "warning" : "slate"
          }
        >
          {frozen ? "Disabled" : t.status === "ACTIVE" ? "Active" : t.status}
        </Badge>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">{num(t.users)}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {num(t.activeUsers30d)}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {num(t.openCases)}
      </td>
      <td className="px-3 py-2.5">
        {t.latestDuesFile ? date(t.latestDuesFile) : "—"}
      </td>
      <td className="px-3 py-2.5 text-muted-foreground">
        {t.lastSignInAt ? relativeTime(t.lastSignInAt) : "—"}
      </td>
      <td className="px-5 py-2.5 text-right">
        <Button
          variant={frozen ? "outline" : "destructive"}
          size="sm"
          onClick={onToggle}
          disabled={toggle.isPending || (t.status !== "ACTIVE" && !frozen)}
        >
          {toggle.isPending ? "Saving…" : frozen ? "Enable" : "Disable"}
        </Button>
        {toggle.isError && (
          <p className="mt-1 text-2xs text-negative">
            {errorText(toggle.error, "Failed.")}
          </p>
        )}
      </td>
    </tr>
  );
}

const EMPTY: NewTenant = {
  slug: "",
  displayName: "",
  tier: "EXCEL",
  adminEmpId: "",
  adminName: "",
  adminEmail: "",
  adminMobile: "",
  adminTempPassword: "",
};

function CreateTenantForm({ onClose }: { onClose: () => void }) {
  const create = useCreateTenant();
  const [f, setF] = useState<NewTenant>(EMPTY);
  const set =
    (k: keyof NewTenant) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setF((s) => ({ ...s, [k]: e.target.value }));

  const slugOk = /^[a-z][a-z0-9-]{2,30}$/.test(f.slug);
  const canSubmit =
    slugOk &&
    f.displayName.trim() &&
    f.adminEmpId.trim().length >= 3 &&
    f.adminName.trim() &&
    f.adminTempPassword.length >= 8;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(f, {
      onSuccess: () => {
        setF(EMPTY);
        onClose();
      },
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>New tenant</CardTitle>
        <CardDescription>
          Give the first admin the temporary password yourself; they must choose
          their own at first sign-in. They also get an account email, which
          never contains a password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="nt-name">Lender name</Label>
              <Input
                id="nt-name"
                value={f.displayName}
                onChange={set("displayName")}
                placeholder="MS Fincap Pvt Ltd"
              />
            </div>
            <div>
              <Label htmlFor="nt-slug">Tenant code</Label>
              <Input
                id="nt-slug"
                className="font-mono"
                value={f.slug}
                onChange={(e) =>
                  setF((s) => ({ ...s, slug: e.target.value.toLowerCase() }))
                }
                placeholder="msfc"
              />
              <p
                className={`mt-1 text-2xs ${f.slug && !slugOk ? "text-negative" : "text-muted-foreground"}`}
              >
                What users type at sign-in. 3–31 lowercase letters, digits or
                hyphens. Cannot be changed.
              </p>
            </div>
            <div>
              <Label htmlFor="nt-tier">Tier</Label>
              <select
                id="nt-tier"
                value={f.tier}
                onChange={set("tier")}
                className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-navy-900"
              >
                <option value="EXCEL">Excel (file uploads)</option>
                <option value="GRAVITON">Graviton (LMS-fed)</option>
              </select>
            </div>
          </div>
          <p className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
            First admin
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="nt-emp">Emp ID</Label>
              <Input
                id="nt-emp"
                className="font-mono"
                value={f.adminEmpId}
                onChange={(e) =>
                  setF((s) => ({
                    ...s,
                    adminEmpId: e.target.value.toUpperCase(),
                  }))
                }
              />
            </div>
            <div>
              <Label htmlFor="nt-aname">Full name</Label>
              <Input
                id="nt-aname"
                value={f.adminName}
                onChange={set("adminName")}
              />
            </div>
            <div>
              <Label htmlFor="nt-pw">Temporary password</Label>
              <Input
                id="nt-pw"
                type="password"
                autoComplete="new-password"
                value={f.adminTempPassword}
                onChange={set("adminTempPassword")}
              />
            </div>
            <div>
              <Label htmlFor="nt-email">Email</Label>
              <Input
                id="nt-email"
                type="email"
                value={f.adminEmail}
                onChange={set("adminEmail")}
              />
            </div>
            <div>
              <Label htmlFor="nt-mobile">Mobile</Label>
              <Input
                id="nt-mobile"
                value={f.adminMobile}
                onChange={set("adminMobile")}
                placeholder="+91…"
              />
            </div>
          </div>
          {create.isError && (
            <p className="text-sm text-negative">
              {errorText(create.error, "Could not create the tenant.")}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || create.isPending}
            >
              {create.isPending ? "Creating…" : "Create tenant"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
